/**
 * Lazy Firebase initialisation for the push feature.
 *
 * The Firebase modular SDK is roughly 13 KB gzipped — still worth deferring
 * until the user is logged in and opts in to push notifications. We dynamic-
 * import the SDK on first use and memoise both the `FirebaseApp` instance
 * and the `Messaging` instance so subsequent calls are cheap.
 *
 * The Firebase web-app config values are PUBLIC (they ship in your JS
 * bundle on every site that uses Firebase). Security relies on:
 *   - Restricting the API key in GCP Console to your origins, and
 *   - The VAPID key (which is bound to your sender id and origin).
 */

import type { FirebaseApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';

interface FirebaseEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}

function readEnv(): FirebaseEnv | null {
  const raw = import.meta.env;
  const config: FirebaseEnv = {
    apiKey: String(raw.VITE_FIREBASE_API_KEY ?? ''),
    authDomain: String(raw.VITE_FIREBASE_AUTH_DOMAIN ?? ''),
    projectId: String(raw.VITE_FIREBASE_PROJECT_ID ?? ''),
    messagingSenderId: String(raw.VITE_FIREBASE_MESSAGING_SENDER_ID ?? ''),
    appId: String(raw.VITE_FIREBASE_APP_ID ?? ''),
    vapidKey: String(raw.VITE_FIREBASE_VAPID_KEY ?? ''),
  };
  // All six values are required — bail early if Firebase isn't provisioned
  // for the current environment yet (e.g. local dev without secrets).
  if (Object.values(config).some((v) => !v)) return null;
  return config;
}

let cachedEnv: FirebaseEnv | null | undefined;
let appPromise: Promise<FirebaseApp | null> | null = null;
let messagingPromise: Promise<Messaging | null> | null = null;

export function getFirebaseEnv(): FirebaseEnv | null {
  if (cachedEnv === undefined) cachedEnv = readEnv();
  return cachedEnv;
}

export function getVapidKey(): string | null {
  return getFirebaseEnv()?.vapidKey ?? null;
}

export async function getFirebaseApp(): Promise<FirebaseApp | null> {
  if (!appPromise) {
    appPromise = (async () => {
      const env = getFirebaseEnv();
      if (!env) {
        console.error('[push] Firebase initialization skipped — configuration is incomplete');
        return null;
      }
      console.info('[push] initializing Firebase', {
        projectId: env.projectId,
        messagingSenderId: env.messagingSenderId,
        authDomain: env.authDomain,
      });
      const { initializeApp, getApps, getApp } = await import('firebase/app');
      // Hot-reload may have kept the app alive — reuse it.
      if (getApps().length > 0) return getApp();
      return initializeApp({
        apiKey: env.apiKey,
        authDomain: env.authDomain,
        projectId: env.projectId,
        messagingSenderId: env.messagingSenderId,
        appId: env.appId,
      });
    })();
  }
  return appPromise;
}

/**
 * Returns the `Messaging` instance when the current browser supports FCM,
 * or `null` otherwise. `isSupported()` covers private-mode quirks and old
 * iOS Safari without us needing UA sniffing.
 */
export async function getMessagingIfSupported(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const app = await getFirebaseApp();
      if (!app) return null;
      const { getMessaging, isSupported } = await import('firebase/messaging');
      try {
        if (!(await isSupported())) return null;
        return getMessaging(app);
      } catch (error) {
        console.error('[push] Firebase Messaging initialization failed', error);
        return null;
      }
    })();
  }
  return messagingPromise;
}
